/**
 * TabNavigation — Tab bar (Map / Chart / Tabular) + global filter toggle & buttons.
 *
 * Extracted from school.tsx for cleanliness.
 */

import React from 'react';
import { Button } from '@mui/material';
import FilterAltIcon from '@mui/icons-material/FilterAlt';
import type { ActiveTab } from '../../types';
import type { UseFiltersReturn } from '../../hooks/useFilters';
import type { UseKPIDataReturn } from '../../hooks/useKPIData';

interface TabNavigationProps {
  activeTab: ActiveTab;
  setActiveTab: (tab: ActiveTab) => void;
  filters: UseFiltersReturn;
  kpiData: UseKPIDataReturn;
}

const tabs: { key: ActiveTab; label: string }[] = [
  { key: 'map', label: 'Map Data Report' },
  { key: 'chart', label: 'Chart Visualization' },
  { key: 'tabular', label: 'Tabular Data Report' },
];

export default function TabNavigation({ activeTab, setActiveTab, filters, kpiData }: TabNavigationProps) {
  const {
    category, kpi, globalFilters, savedFilters, filtersEnabled, filterConfigs,
    setGlobalFilters, setSavedFilters, setFiltersEnabled, setFilterModalOpen,
  } = filters;

  return (
    <div className="w-full">
      <div className="flex justify-between items-center border-b border-gray-200">
        {/* Tabs */}
        <div className="flex">
          {tabs.map(t => (
            <button
              key={t.key}
              onClick={() => setActiveTab(t.key)}
              className={`px-6 py-3 font-medium text-sm ${
                activeTab === t.key
                  ? 'text-purple-600 border-b-2 border-purple-600 bg-purple-50'
                  : 'text-gray-500 hover:text-gray-700'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Global Filter Controls */}
        {category && kpi && filterConfigs.length > 0 && (
          <div className="flex items-center gap-3 pr-4">
            {/* Toggle */}
            {globalFilters.length > 0 && (
              <div className="flex items-center gap-2 border border-gray-300 rounded-lg px-3 py-1 bg-white">
                <span className="text-xs text-gray-600">Filters</span>
                <button
                  onClick={() => {
                    if (filtersEnabled) {
                      setSavedFilters(globalFilters);
                      setGlobalFilters([]);
                    } else {
                      setGlobalFilters(savedFilters);
                    }
                    setFiltersEnabled(!filtersEnabled);
                  }}
                  className={`w-10 h-5 rounded-full transition-colors relative ${filtersEnabled ? 'bg-green-500' : 'bg-gray-300'}`}
                >
                  <span
                    className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform ${
                      filtersEnabled ? 'translate-x-5' : 'translate-x-0.5'
                    }`}
                  />
                </button>
              </div>
            )}

            {/* Clear */}
            {globalFilters.length > 0 && (
              <Button
                size="small"
                variant="outlined"
                color="secondary"
                onClick={() => { setGlobalFilters([]); setSavedFilters([]); }}
                sx={{ textTransform: 'none', fontSize: '12px' }}
              >
                Clear Filters ({globalFilters.length})
              </Button>
            )}

            {/* Open modal */}
            <Button
              variant="contained"
              startIcon={<FilterAltIcon />}
              onClick={() => setFilterModalOpen(true)}
              sx={{
                textTransform: 'none',
                backgroundColor: filtersEnabled ? '#6A1786' : '#9ca3af',
                borderRadius: '8px',
                padding: '8px 20px',
                '&:hover': { backgroundColor: filtersEnabled ? '#551365' : '#6b7280' },
              }}
            >
              Filter Options
              {globalFilters.length > 0 && (
                <span className="ml-2 bg-white text-purple-700 px-2 py-0.5 rounded-full text-xs font-semibold">
                  {globalFilters.length}
                </span>
              )}
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}
